import { ArrowUpRight, Check } from 'lucide-react'

import {
  type PetDiscount,
  type PortalPlan,
  REGIONS_BY_ID,
  type RegionId,
  type SubscriptionPlan,
} from '@snootsvet/shared/portal'

import { PlanOptionCard } from '@/customer/components/billing/plan-option-card'
import {
  DISCOUNT_LABELS,
  describePlanPrice,
  findPlanDiscount,
  PERK_COPY,
} from '@/customer/components/billing/subscription-plans'
import { cn } from '@/customer/lib/utils'

/**
 * Poster-outline radio picker for the plans the member's region sells. Controlled: the parent
 * owns the selection. Cards follow the order `GET /plans` returns, best value first.
 */
export function SubscriptionPlanPicker({
  value,
  onValueChange,
  region,
  plans,
  discounts = [],
  'aria-label': ariaLabel,
  className,
}: {
  value: SubscriptionPlan
  onValueChange: (value: SubscriptionPlan) => void
  region: RegionId
  /** The plans the region sells, from `GET /plans`, in display order. */
  plans: readonly PortalPlan[]
  /** The pet's eligible discounts, from `GET /pets/:id/discounts`. */
  discounts?: readonly PetDiscount[]
  'aria-label'?: string
  className?: string
}) {
  return (
    <div className={cn('flex flex-col gap-6', className)}>
      <fieldset
        className="flex flex-col gap-4 md:flex-row md:items-stretch"
        aria-label={ariaLabel ?? 'Membership plan'}
      >
        {plans.map((plan) => {
          const discount = findPlanDiscount(discounts, plan.plan)
          const pricing = describePlanPrice(plans, plan.plan, region, discount?.percentOff)
          if (!pricing) return null

          return (
            <PlanOptionCard
              key={plan.plan}
              name="subscription-plan"
              value={plan.plan}
              title={plan.name}
              pricing={pricing}
              fullPrice={discount ? describePlanPrice(plans, plan.plan, region)?.price : undefined}
              discountNote={
                discount ? `${discount.percentOff}% ${DISCOUNT_LABELS[discount.kind]}` : undefined
              }
              checked={plan.plan === value}
              onSelect={() => onValueChange(plan.plan)}
            >
              {plan.perks.length ? (
                <ul className="mt-4 flex flex-col gap-1 text-[14px] leading-snug">
                  {plan.perks.map((perk) => (
                    <li key={perk} className="flex items-start gap-2">
                      <Check className="mt-0.5 size-4 shrink-0" aria-hidden />
                      {PERK_COPY[perk]}
                    </li>
                  ))}
                </ul>
              ) : null}
            </PlanOptionCard>
          )
        })}
      </fieldset>

      {/* Marketing owns the full "what's included" copy; the picker
          defers to their pricing page rather than mirroring the list. */}
      <a
        href={REGIONS_BY_ID[region].pricingUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 self-center text-body-md text-foreground/70 underline underline-offset-4 hover:text-foreground"
      >
        Compare plans
        <ArrowUpRight className="size-4" aria-hidden />
      </a>
    </div>
  )
}
